/**
 * studio-genome-compose — the plugin-AGNOSTIC compose primitives shared by the
 * eager route-only phase (`genome.ts`) and the lazy full-graph phase
 * (`genome-full.ts`) (#705 S3, the generate-split cutover).
 *
 * The studio's ENTRY resolves routes from a schema-free ROUTE-only genome
 * (`genome.ts` — the 8 generated clusters composed as `studioAppRoutePlugin`s, so
 * NO `generate()` / schema literals / strand kernel land on the entry). The FULL
 * 16-kind graph — schemas + stores + services + behavioural kinds — is composed
 * LAZILY in `genome-full.ts`, consumed only OFF the entry (the genome-graph
 * panel/PERCEIVE, the agent catalog, restage/install, the census + gates).
 *
 * Both phases build against the SAME identity + the SAME compose helpers. Those
 * live HERE — a pure module that imports only `GenomeBuilderV3` + TYPES, so
 * `genome.ts` can reach the helpers WITHOUT statically reaching `genome-full.ts`
 * (which would drag the full graph back onto the entry). Each phase supplies its
 * OWN `studioPlugins` / `studioGenome` / `composeStudioGenome()`; only the
 * plugin-list is different, never the identity or the builder assembly.
 *
 * ENTRY-SAFE: no plugin imports, no `generate()`, no React, no `./validate.js`
 * (the eager phase uses {@link composeGenomeUnvalidated}; the SDK's default
 * validator registration is tree-shaken off the entry — #518).
 *
 * @module @ubiclouds/v5-studio/genome/studio-genome-compose
 */

import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';
// #865 S1 (gate 8): the eager no-validate compose goes through the GRANULAR
// `@ubiclouds/v5-sdk/genome/compose` subpath — the pure fold the builder itself
// delegates to. Rollup keeps every method of an INSTANTIATED class, so routing
// the eager boot through `GenomeBuilderV3` dragged its whole fluent surface
// (plus the validator slot) onto the entry chunk. The VALIDATING mouth now lives
// in `./studio-genome-compose-validated.js`, which the entry never statically
// reaches (this module DOES land in the entry chunk, so any symbol it exports
// must materialise there even when only a lazy chunk imports it).
import { composePluginEntities } from '@ubiclouds/v5-sdk/genome/compose';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The studio genome id. */
export const STUDIO_GENOME_ID = 'v5-studio';

/**
 * THE studio genome metadata — the SINGLE source for `id`/`name`/`version`. Both
 * phases compose against this one literal so a recompose (the agent restage / a
 * Store install) never drifts from the module-scope genome's identity.
 */
export const STUDIO_GENOME_META = Object.freeze({
  id: STUDIO_GENOME_ID,
  name: 'Genome v5 Studio',
  version: '5.0.0',
});

/**
 * Compose a genome from a non-empty plugin list WITHOUT running the v3 cross-ref
 * validator (#518, gate 8): `.build()` statically needs `./validate.js` (~6 KB
 * min), and the studio's EAGER entry composes the module-scope `studioGenome` in
 * `genome.ts` — so validating eagerly would land the validator on the entry
 * chunk. The eager boot uses THIS no-validate build (boot safety is the `main.tsx`
 * ErrorBoundary + the validating restage/install paths + the validating build
 * asserted in the genome/one-genome gate tests); the lazy restage/install paths
 * keep the FULL validating {@link composeGenome}.
 */
export function composeGenomeUnvalidated(
  meta: { id: string; name: string; version: string },
  plugins: readonly ComposableGenomePlugin[],
): GenomeV3 {
  // #865 S1 — the pure FOLD, not the builder. `composePluginEntities` is the very
  // function `GenomeBuilderV3.use()`/`.buildUnvalidated()` delegate to (a
  // compose-identity test pins the two deep-equal on BOTH studio plugin sets), so
  // this is the same composition semantics with none of the class's weight.
  if (plugins.length === 0) {
    throw new Error('studio genome: at least one plugin must be composed');
  }
  return composePluginEntities(meta, plugins);
}

